import PropTypes from 'prop-types'
import React from 'react'
import css from './Detail.module.css'
import cn from 'classnames'
import pluralize from 'pluralize'
import { Text, Button, Chevron } from '@segment/react-ui-library'
import {
  Browser,
  Server,
  Mobile,
  Cloud,
  Track,
  Identify,
  Screenpage,
  Group,
  Alias
} from '@segment/icons/react'
import Link from 'ui/Link'
import { color } from 'ui/variables'
import Tile from '../Catalog/Tile'
import SourcesPopover from './SourcesPopover'

const BackLink = ({ workspaceSlug }) => (
  <div className={css.backLink}>
    <Link href={`/${workspaceSlug}/integrations/catalog`} underline={false}>
      <Chevron aim='left' marginRight='8px' fill='currentColor' /> All Integrations
    </Link>
  </div>
)

const SubSection = ({ heading, children }) => {
  return (
    <div>
      <div className={css.separator} />

      <Text is='h2' textStyle='display3' marginBottom='8px'>{heading}</Text>
      <p>{children}</p>
    </div>
  )
}

const Feature = ({ className, heading, children, Icon }) => {
  return (
    <div className={cn(css.feature, className)}>
      <h3 className={css.featureHeading}>
        <div className={css.featureIcon}>
          <Icon color={color.green[500]} />
        </div>
        {heading}
      </h3>
      <p>{children}</p>
    </div>
  )
}

const Source = (props) => <Feature {...props} className={css.source} />
const Method = (props) => <Feature {...props} className={css.method} />

export default function Detail (props) {
  const {
    workspaceSlug,
    canAddSources,
    integration,
    compatibleSources,
    connectedSources,
    searchTerm,
    onSearchChange,
    onButtonClick,
    onAddSourceClick,
    onSourceClick
  } = props

  return (
    <div className={css.main}>
      <BackLink workspaceSlug={workspaceSlug} />

      <div className={css.sheet}>
        <div>
          <Tile
            image={integration.logos.default}
            title={integration.name}
            subtitle={integration.categories[0]}
            tag={integration.public ? null : 'private'} />

          <div className={css.button}>
            <SourcesPopover
              title='Compatible Sources'
              sources={compatibleSources}
              workspaceSlug={workspaceSlug}
              integrationSlug={integration.slug}
              canAddSources={canAddSources}
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
              onOpen={() => onButtonClick(integration.slug, 'connect')}
              onAddSourceClick={(isEmpty) => onAddSourceClick(integration.slug, isEmpty ? 'empty' : 'connect')}
              onSourceClick={(sourceSlug) => onSourceClick(integration.slug, sourceSlug, 'connect')}>
              <Button primary display='block' width='100%'>Connect</Button>
            </SourcesPopover>
          </div>

          {connectedSources.length > 0 &&
            <div className={css.button}>
              <SourcesPopover
                title='Connected Sources'
                sources={connectedSources}
                workspaceSlug={workspaceSlug}
                integrationSlug={integration.slug}
                canAddSources={canAddSources}
                searchTerm={searchTerm}
                onSearchChange={onSearchChange}
                onOpen={() => onButtonClick(integration.slug, 'already_connected')}
                onAddSourceClick={() => onAddSourceClick(integration.slug, 'already_connected')}
                onSourceClick={(sourceSlug) => onSourceClick(integration.slug, sourceSlug, 'already_connected')}>
                <Button secondary display='block' width='100%'>
                  {pluralize('Source', connectedSources.length, true)} connected
                </Button>
              </SourcesPopover>
            </div>
          }

          <h2 className={css.relatedLinksHeading}>
            Related links
          </h2>
          <ul>
            {integration.website &&
              <li>
                <Link href={integration.website} rel='external'>
                  {integration.name} Website
                </Link>
              </li>
            }
            <li>
              <Link href={`https://segment.com/docs/integrations/${integration.slug}/`} rel='external'>
                Documentation
              </Link>
            </li>
          </ul>
        </div>

        <div className={css.rightColumn}>
          <Text is='h1' textStyle='display1' marginBottom='16px'>
            {integration.name}
          </Text>
          <p>{integration.description}</p>

          <SubSection heading='Sources'>
            The Segment - {integration.name} integration can receive data from
            the following sources:
          </SubSection>

          <div className={css.sources}>
            {integration.platforms.browser &&
              <Source heading='Website' Icon={Browser}>
                Analytics.js javascript library
              </Source>
            }
            {integration.platforms.server &&
              <Source heading='Server' Icon={Server}>
                Server-side libraries like Node, Go, PHP, Ruby
              </Source>
            }
            {integration.platforms.mobile &&
              <Source heading='Mobile' Icon={Mobile}>
                Our iOS and Android libraries
              </Source>
            }
            {integration.platforms.cloud &&
              <Source heading='Cloud' Icon={Cloud}>
                Cloud services like Salesforce, Facebook Ads and more
              </Source>
            }
          </div>

          <SubSection heading='Methods'>
            Each method describes a distinct type of information about a customer.
            The {integration.name} integration supports the following Segment API methods:
          </SubSection>

          <div className={css.methods}>
            {integration.methods.track &&
              <Method heading='Track' Icon={Track}>
                The track API call is how you record any actions your users
                perform, along with any properties that describe the action.
              </Method>
            }
            {integration.methods.identify &&
              <Method heading='Identify' Icon={Identify}>
                The identify API call ties a customer and their actions to a
                recognizable ID and traits like their email, name, etc.
              </Method>
            }
            {integration.methods.pageview &&
              <Method heading='Page/Screen' Icon={Screenpage}>
                The page/screen API call lets you record whenever a user views a
                pages in a browser, or a screen in a mobile app, along with any
                properties of that page/screen.
              </Method>
            }
            {integration.methods.group &&
              <Method heading='Group' Icon={Group}>
                The group API call is how you associate an individual user with
                a group—be it a company, organization, account, project, team, etc.
              </Method>
            }
            {integration.methods.alias &&
              <Method heading='Alias' Icon={Alias}>
                The alias API call is used to merge two user identities, for
                example when tying a user’s behavior before and after signing up.
              </Method>
            }
          </div>
        </div>
      </div>

      <div className={css.textCenter}>
        <BackLink workspaceSlug={workspaceSlug} />
      </div>
    </div>
  )
}

Detail.displayName = 'Detail'

Detail.propTypes = {
  workspaceSlug: PropTypes.string.isRequired,
  isLegacyWorkspace: PropTypes.bool.isRequired,
  canAddSources: PropTypes.bool.isRequired,
  integration: PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    website: PropTypes.string,
    categories: PropTypes.arrayOf(PropTypes.string).isRequired,
    platforms: PropTypes.shape({
      browser: PropTypes.bool,
      mobile: PropTypes.bool,
      server: PropTypes.bool,
      cloud: PropTypes.bool
    }).isRequired,
    methods: PropTypes.shape({
      alias: PropTypes.bool,
      group: PropTypes.bool,
      identify: PropTypes.bool,
      pageview: PropTypes.bool,
      track: PropTypes.bool
    }).isRequired,
    logos: PropTypes.shape({
      default: PropTypes.string.isRequired
    }).isRequired,
    public: PropTypes.bool.isRequired
  }).isRequired,
  compatibleSources: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired
  })).isRequired,
  connectedSources: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired
  })).isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onButtonClick: PropTypes.func.isRequired,
  onAddSourceClick: PropTypes.func.isRequired,
  onSourceClick: PropTypes.func.isRequired
}



// WEBPACK FOOTER //
// ./components/IntegrationsCatalog/Detail.js